import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  GitPullRequest,
  Loader2,
  Monitor,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Textarea } from "@/ui/textarea";
import {
  FormCard,
  FormStackedRow,
  InlineMessage,
  SettingsHeader,
} from "@/components/settings/settings-ui";
import {
  envToText,
  parseEnvText,
  useCreatePreviewConfigMutation,
  useDeletePreviewConfigMutation,
  usePreviewConfigsQuery,
  useUpdatePreviewConfigMutation,
  type PreviewAppInfo,
  type PreviewConfigInput,
  type PreviewConfigRow,
} from "@/data/preview-configs";

interface PreviewsSectionProps {
  workspaceSlug?: string | null;
  canManage: boolean;
}

const BOOT_PLACEHOLDER = `# Runs inside the VM after we check out the PR (cwd = repo root).
# Print a line matching the ready pattern once the app is up.
npm ci
npm run build
npm start`;

export function PreviewsSection({
  workspaceSlug,
  canManage,
}: PreviewsSectionProps) {
  const query = usePreviewConfigsQuery(workspaceSlug);
  const createMutation = useCreatePreviewConfigMutation(workspaceSlug ?? null);
  const updateMutation = useUpdatePreviewConfigMutation(workspaceSlug ?? null);
  const deleteMutation = useDeletePreviewConfigMutation(workspaceSlug ?? null);
  // null = nothing open, "new" = blank create form, otherwise the config id.
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const configs = query.data?.configs ?? [];
  const app = query.data?.app;

  async function saveNew(repo: string, config: PreviewConfigInput) {
    setError(null);
    try {
      await createMutation.mutateAsync({ repo, ...config });
      setEditing(null);
      toast.success("Preview config saved");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error("Failed to save preview config");
    }
  }

  async function saveExisting(id: string, config: PreviewConfigInput) {
    setError(null);
    try {
      await updateMutation.mutateAsync({ id, config });
      setEditing(null);
      toast.success("Preview config saved");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error("Failed to save preview config");
    }
  }

  async function removeConfig(row: PreviewConfigRow) {
    setConfirmId(null);
    setError(null);
    try {
      await deleteMutation.mutateAsync(row.id);
      if (editing === row.id) setEditing(null);
      toast.success("Preview config removed");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error("Failed to remove preview config");
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="Previews"
        description="Give every pull request a live, one-click preview of your app. Install the Parable GitHub App on a repo, describe how to build it, and each PR gets a link that opens the running app in your browser."
      />

      <div className="space-y-6">
        {error ? <InlineMessage variant="error">{error}</InlineMessage> : null}

        <ConnectCard app={app} loading={query.isPending && !!workspaceSlug} />

        <FormCard>
          <FormStackedRow
            label="Repositories"
            hint="Each configured repo gets previews on new and updated pull requests."
          >
            {query.isPending && workspaceSlug ? (
              <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading previews
              </div>
            ) : configs.length === 0 ? (
              <div className="border-t border-border py-3 text-[13px] text-muted-foreground">
                No repositories configured yet.
              </div>
            ) : (
              <div className="divide-y divide-border border-t border-border">
                {configs.map((config) => (
                  <div key={config.id}>
                    <ConfigRow
                      config={config}
                      canManage={canManage}
                      open={editing === config.id}
                      armed={confirmId === config.id}
                      busy={
                        deleteMutation.isPending &&
                        deleteMutation.variables === config.id
                      }
                      onToggleOpen={() =>
                        setEditing((cur) =>
                          cur === config.id ? null : config.id,
                        )
                      }
                      onArm={() => setConfirmId(config.id)}
                      onDisarm={() => setConfirmId(null)}
                      onDelete={() => void removeConfig(config)}
                    />
                    {editing === config.id ? (
                      <div className="pb-4">
                        <ConfigEditor
                          key={config.id}
                          initial={config}
                          canManage={canManage}
                          saving={updateMutation.isPending}
                          onCancel={() => setEditing(null)}
                          onSave={(cfg) => void saveExisting(config.id, cfg)}
                        />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </FormStackedRow>
        </FormCard>

        {canManage ? (
          editing === "new" ? (
            <FormCard>
              <FormStackedRow label="Add repository">
                <ConfigEditor
                  key="new"
                  canManage={canManage}
                  saving={createMutation.isPending}
                  onCancel={() => setEditing(null)}
                  onSaveNew={(repo, cfg) => void saveNew(repo, cfg)}
                />
              </FormStackedRow>
            </FormCard>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null);
                setEditing("new");
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Add repository
            </Button>
          )
        ) : (
          <InlineMessage variant="info">
            Only workspace admins can configure previews.
          </InlineMessage>
        )}
      </div>
    </div>
  );
}

function ConnectCard({
  app,
  loading,
}: {
  app: PreviewAppInfo | undefined;
  loading: boolean;
}) {
  return (
    <FormCard>
      <FormStackedRow
        label="GitHub App"
        hint="Install the Parable App on the repositories you want previews for. We only request pull-request and read-only contents access."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={loading || !app}
            onClick={() => {
              if (app) window.open(app.install_url, "_blank", "noopener");
            }}
          >
            <GitPullRequest className="h-3.5 w-3.5" />
            Install the Parable App
          </Button>
          {app && !app.configured ? (
            <span className="text-[12px] text-amber-300">
              Previews aren’t enabled for this deployment yet — contact your
              Parable admin.
            </span>
          ) : (
            <span className="text-[12px] text-muted-foreground">
              After installing, add the repo below.
            </span>
          )}
        </div>
      </FormStackedRow>
    </FormCard>
  );
}

function ConfigRow({
  config,
  canManage,
  open,
  armed,
  busy,
  onToggleOpen,
  onArm,
  onDisarm,
  onDelete,
}: {
  config: PreviewConfigRow;
  canManage: boolean;
  open: boolean;
  armed: boolean;
  busy: boolean;
  onToggleOpen: () => void;
  onArm: () => void;
  onDisarm: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="grid items-center gap-3 py-3 md:grid-cols-[minmax(0,1fr)_auto]">
      <button
        type="button"
        onClick={onToggleOpen}
        className="flex min-w-0 items-center gap-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono text-[13px] font-medium text-foreground">
              {config.repo}
            </span>
            <StatusBadge
              connected={config.connected}
              enabled={config.enabled}
            />
          </div>
          <div className="truncate text-[12px] text-muted-foreground">
            base {config.base_image} · ready&nbsp;
            <span className="font-mono">/{config.ready_pattern}/</span>
          </div>
        </div>
      </button>
      {canManage ? (
        <Button
          type="button"
          size="sm"
          variant={armed ? "destructive" : "outline"}
          disabled={busy}
          onClick={() => (armed ? onDelete() : onArm())}
          onBlur={onDisarm}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
          {armed ? "Confirm" : "Remove"}
        </Button>
      ) : (
        <div />
      )}
    </div>
  );
}

function StatusBadge({
  connected,
  enabled,
}: {
  connected: boolean;
  enabled: boolean;
}) {
  if (!enabled) {
    return (
      <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground">
        Paused
      </span>
    );
  }
  const className = connected
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
    : "border-amber-500/30 bg-amber-500/10 text-amber-200";
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[11px] ${className}`}
    >
      {connected ? "Connected" : "Awaiting first event"}
    </span>
  );
}

type EditorProps = {
  initial?: PreviewConfigRow;
  canManage: boolean;
  saving: boolean;
  onCancel: () => void;
} & (
  | { onSave: (config: PreviewConfigInput) => void; onSaveNew?: never }
  | {
      onSaveNew: (repo: string, config: PreviewConfigInput) => void;
      onSave?: never;
    }
);

function ConfigEditor(props: EditorProps) {
  const { initial, canManage, saving, onCancel } = props;
  const isNew = !initial;
  const [repo, setRepo] = useState(initial?.repo ?? "");
  const [bootCommand, setBootCommand] = useState(initial?.boot_command ?? "");
  const [readyPattern, setReadyPattern] = useState(
    initial?.ready_pattern ?? "",
  );
  const [failPattern, setFailPattern] = useState(initial?.fail_pattern ?? "");
  const [healthProcess, setHealthProcess] = useState(
    initial?.health_process ?? "",
  );
  const [baseImage, setBaseImage] = useState(
    initial?.base_image ?? "radius-base",
  );
  const [patchOverlay, setPatchOverlay] = useState(
    initial?.patch_overlay ?? "",
  );
  const [envText, setEnvText] = useState(envToText(initial?.env ?? {}));
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const repoValid = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/.test(repo.trim());
  const canSave =
    canManage &&
    !saving &&
    bootCommand.trim().length > 0 &&
    readyPattern.trim().length > 0 &&
    (!isNew || repoValid);

  function collect(): PreviewConfigInput {
    return {
      base_image: baseImage.trim() || "radius-base",
      boot_command: bootCommand,
      ready_pattern: readyPattern.trim(),
      fail_pattern: failPattern.trim() || null,
      health_process: healthProcess.trim() || null,
      patch_overlay: patchOverlay.length ? patchOverlay : null,
      env: parseEnvText(envText),
      enabled,
    };
  }

  function save() {
    if (!canSave) return;
    if (props.onSaveNew) props.onSaveNew(repo.trim(), collect());
    else props.onSave(collect());
  }

  return (
    <div className="space-y-4 rounded-xl border border-border bg-background p-4">
      {isNew ? (
        <Field
          label="Repository"
          hint="owner/name, exactly as it appears on GitHub."
        >
          <Input
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            placeholder="acme/desktop-app"
            className="font-mono"
          />
        </Field>
      ) : null}

      <Field
        label="Boot command"
        hint="Runs inside the VM after we check out the PR. Print a line matching the ready pattern once the app is up."
      >
        <Textarea
          value={bootCommand}
          onChange={(e) => setBootCommand(e.target.value)}
          placeholder={BOOT_PLACEHOLDER}
          rows={7}
          className="font-mono text-[12px]"
          spellCheck={false}
        />
      </Field>

      <Field
        label="Ready signal"
        hint="A regex we watch your boot output for to know the app has finished starting."
      >
        <Input
          value={readyPattern}
          onChange={(e) => setReadyPattern(e.target.value)}
          placeholder="APP READY"
          className="font-mono"
        />
      </Field>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground"
      >
        {showAdvanced ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        Advanced
      </button>

      {showAdvanced ? (
        <div className="space-y-4 border-l-2 border-border pl-4">
          <Field
            label="Failure signal"
            hint="Optional. A regex that means the boot failed, so we stop waiting early."
          >
            <Input
              value={failPattern}
              onChange={(e) => setFailPattern(e.target.value)}
              placeholder="build failed|exited before ready"
              className="font-mono"
            />
          </Field>
          <Field
            label="Health-check process"
            hint="Optional. A process name we confirm is still running after resume (e.g. Electron)."
          >
            <Input
              value={healthProcess}
              onChange={(e) => setHealthProcess(e.target.value)}
              placeholder="Electron"
              className="font-mono"
            />
          </Field>
          <Field
            label="Patch overlay"
            hint="Optional. A unified diff we apply with git apply after checkout — keep preview-only tweaks here instead of committing them to your repo."
          >
            <Textarea
              value={patchOverlay}
              onChange={(e) => setPatchOverlay(e.target.value)}
              placeholder="diff --git a/... b/..."
              rows={5}
              className="font-mono text-[12px]"
              spellCheck={false}
            />
          </Field>
          <Field
            label="Environment"
            hint="Optional. One KEY=value per line, exported before the boot command runs."
          >
            <Textarea
              value={envText}
              onChange={(e) => setEnvText(e.target.value)}
              placeholder="AUTH_BYPASS=1"
              rows={3}
              className="font-mono text-[12px]"
              spellCheck={false}
            />
          </Field>
          <Field
            label="Base image"
            hint="The prepared VM image with your toolchain. Set up with your Parable contact — leave as-is if unsure."
          >
            <Input
              value={baseImage}
              onChange={(e) => setBaseImage(e.target.value)}
              className="font-mono"
            />
          </Field>
          <label className="flex items-center gap-2 text-[13px] text-foreground">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-3.5 w-3.5 accent-emerald-500"
            />
            Previews enabled for this repo
          </label>
        </div>
      ) : null}

      <div className="flex items-center gap-2 pt-1">
        <Button type="button" disabled={!canSave} onClick={save}>
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Monitor className="h-3.5 w-3.5" />
          )}
          {isNew ? "Save preview config" : "Save changes"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div>
        <div className="text-[13px] font-medium text-foreground">{label}</div>
        {hint ? (
          <div className="mt-0.5 text-[12px] text-muted-foreground">{hint}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}
